import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { API, axios } from '../../utils/api';
import { caseDellaVoce, caseEsito, etichettaTipo, isoGiorno, limitiData, messaggioErrore, rigaVoce, prezzoBreve, TIPI } from '../../utils/agenda';
import { adessoRoma, linkChiama, linkWhatsApp, MOTIVO_CON_NOTA, motiviVisitaStaff } from '../../utils/percorso';
import { Iniziale, usePersone } from './Persone';
import EsitoTelefonata from './EsitoTelefonata';
import { ConsiglioRiga } from './Consiglio';
import CosaVoce from './CosaVoce';
import AzioniVisita from './AzioniVisita';
import { visitaDaVoce } from '../../utils/visite';

// Una voce dell'agenda come card: tipo in maiuscoletto colorato, titolo serif, riga cliente/agenzia, iniziale di chi
// la fa, spunta "fatta". Le voci che riguardano un immobile (visite, telefonate, scadenze della proposta…) hanno la foto
// e il titolo completo dell'immobile; le telefonate il bottone "Esito" con i sette esiti; sotto, il consiglio se c'è.

export const linkVoce = (voce) => {
  if (voce.tipo === 'documento' && voce.cliente_id) return `/admin/clienti/${voce.cliente_id}#documenti`;
  if (voce.immobile_id) return `/immobili/${voce.immobile_id}`;
  if (voce.cliente_id) return `/admin/clienti/${voce.cliente_id}`;
  if (voce.agenzia_id) return `/admin/agenzie/${voce.agenzia_id}`;
  return null;
};

// Quando finisce una visita: ora + durata (voce.dettaglio.durata) o l'ora di base del server (backend/agenda.py,
// DURATA_VISITA); senza ora, a fine giornata.
export const DURATA_VISITA = 60;
export const fineVisita = (voce) => {
  const giorno = voce?.data;
  if (!voce?.ora) return new Date(`${giorno}T23:59:00`);
  const d = new Date(`${giorno}T${voce.ora}:00`);
  const minuti = Number(voce.dettaglio?.durata);
  d.setMinutes(d.getMinutes() + (minuti > 0 ? minuti : DURATA_VISITA));
  return d;
};

// Una visita finita che non ha ancora l'esito: quello che serve non è la spunta "fatta" (il server non la accetta
// più senza esito) ma "Com'è andata?". L'ora è quella di Roma, come in tutta l'agenda. Il conto si fa sulla FINE
// della visita, non sul minuto d'inizio: dalle 10:30 in punto la visita delle 10:30 perdeva "Sposta" e "Annulla",
// e una visita in corso — che si può ancora spostare e annullare, il server lo permette finché non ha l'esito —
// chiedeva l'esito quando ancora non c'era.
export const visitaSenzaEsito = (voce, adesso = adessoRoma()) => Boolean(
  voce && voce.tipo === 'visita' && voce.stato === 'da_fare' && voce.rif?.visita_id && fineVisita(voce) <= adesso);

// Una finestra piccola come "Annullare la visita?": una visita andata male chiede perché, con le stesse caselle del
// cliente (MOTIVI di utils/percorso.js) e due parole facoltative. Il server (correzione dell'altro sviluppatore)
// pretende il motivo: senza, la casa tornerebbe nella ricerca senza il criterio in più.
export const MotiviVisita = ({ voce, onChiudi, onFatto, visitaId }) => {
  const [motivi, setMotivi] = useState([]);
  const [nota, setNota] = useState('');
  const [attesa, setAttesa] = useState(false);
  const id = visitaId || voce?.rif?.visita_id;
  const elenco = motiviVisitaStaff();
  const alterna = (k) => setMotivi((m) => (m.includes(k) ? m.filter((x) => x !== k) : [...m, k]));
  // Cosa manca, scritto: il bottone spento senza spiegazione è lo stesso muro di prima, solo più silenzioso.
  const serveAltro = motivi.includes(MOTIVO_CON_NOTA) && !nota.trim();
  const manca = !motivi.length ? 'Scegliete almeno un motivo: ci serve per cercare meglio.'
    : serveAltro ? 'Con «Altro» scrivete anche cos’era, qui sotto.' : null;
  const salva = async () => {
    if (manca) return;
    setAttesa(true);
    try {
      await axios.put(`${API}/visite/${id}`, { effettuata: true, esito_visita: 'negativo', motivi, note_esito: nota.trim() });
      toast.success('Esito segnato');
      onFatto();
    } catch (e) { toast.error(messaggioErrore(e, 'Esito non salvato. Riprovate.')); setAttesa(false); }
  };
  return createPortal(
    <div className="st st-modale elimina-modale" role="presentation" onMouseDown={(e) => { if (e.target === e.currentTarget) onChiudi(); }}>
      <div className="scatola" role="dialog" aria-modal="true" aria-labelledby="male-titolo" data-testid="motivi-visita">
        <h3 id="male-titolo">Cosa non ha convinto?</h3>
        <p>Ce ne teniamo conto nella ricerca: senza un motivo la casa esce e basta.</p>
        <div className="motivi" role="group" aria-label="Motivi">
          {elenco.map(([k, testo]) => (
            <button type="button" key={k} className={`chip${motivi.includes(k) ? ' on' : ''}`} aria-pressed={motivi.includes(k)} onClick={() => alterna(k)}>{testo}</button>
          ))}
        </div>
        {manca && <p className="manca-motivo" data-testid="manca-motivo">{manca}</p>}
        <label className="parola">{motivi.includes(MOTIVO_CON_NOTA) ? 'Cos’era?' : 'Due parole (facoltative)'}
          <input value={nota} onChange={(e) => setNota(e.target.value)} maxLength={1000}
            required={motivi.includes(MOTIVO_CON_NOTA)} aria-label="Due parole sulla visita" />
        </label>
        <div className="bottoni">
          <button type="button" className="btn sm" onClick={onChiudi}>Indietro</button>
          <button type="button" className="btn sm rosso" onClick={salva} disabled={Boolean(manca) || attesa}>{attesa ? 'Salvo…' : 'Salva l\'esito'}</button>
        </div>
      </div>
    </div>,
    document.querySelector('.mk-staff') || document.body,
  );
};

// "Andata bene / Andata male" come nella scheda dell'immobile (PUT /visite/{id}: effettuata + esito)
export const EsitoVisita = ({ voce, onCambia, piccoli = false }) => {
  const [attesa, setAttesa] = useState(false);
  const [male, setMale] = useState(false);
  const classe = `btn ${piccoli ? 'xs' : 'sm'}`;
  const bene = async () => {
    setAttesa(true);
    try {
      await axios.put(`${API}/visite/${voce.rif.visita_id}`, { effettuata: true, esito_visita: 'positivo' });
      toast.success('Esito segnato');
      if (onCambia) onCambia();
    } catch (e) { toast.error(messaggioErrore(e, 'Esito non salvato. Riprovate.')); } finally { setAttesa(false); }
  };
  return (
    <span className="esito-visita" data-testid={`esito-visita-${voce.id}`}>
      <button type="button" className={classe} disabled={attesa} onClick={bene}>Andata bene</button>
      <button type="button" className={`${classe} rosso-vuoto`} disabled={attesa} onClick={() => setMale(true)}>Andata male</button>
      {male && <MotiviVisita voce={voce} onChiudi={() => setMale(false)} onFatto={() => { setMale(false); if (onCambia) onCambia(); }} />}
    </span>
  );
};

// Le visite non hanno la spunta "fatta": si chiudono sempre dall'esito ("Andata bene" / "Andata male"). Prima della
// fine l'esito non c'è ancora e il server rifiuta la spunta ("Date prima l'esito della visita", 400); dopo la fine
// c'è "Com'è andata?" al suo posto. Era una casella che non poteva funzionare in nessun momento.
// le due attese (il collega e la disponibilità del cliente) si chiudono dall'esito, come le visite: il server
// rifiuta la spunta ("Dite com'è andata con «Esito»", 400) perché l'immobile resterebbe in attesa con una data passata
export const ATTESE = ['attesa_collega', 'attesa_disponibilita'];
export const attesaDelCollega = (voce) => ATTESE.includes(voce?.dettaglio?.sottotipo || '');

export const Spunta = ({ voce, onCambia }) => {
  const fatta = voce.stato === 'fatta';
  if (voce.modificabile === false || voce.tipo === 'visita') return null;
  if (attesaDelCollega(voce)) return null;
  const cambia = async () => {
    try {
      await axios.put(`${API}/agenda/${voce.id}`, { stato: fatta ? 'da_fare' : 'fatta' });
      if (onCambia) onCambia();
    } catch (e) { toast.error(messaggioErrore(e, 'Non riuscito. Riprovate.')); }
  };
  return (
    <button type="button" className={`chk${fatta ? ' on' : ''}`} onClick={cambia} aria-pressed={fatta}
      aria-label={fatta ? 'Segna come da fare' : 'Segna come fatta'} data-testid={`spunta-${voce.id}`}>{fatta ? '✓' : ''}</button>
  );
};

// Un'attività scritta in agenda (chiave "ag:…") si corregge, si riassegna e si cancella: il server accetta
// PUT /api/agenda/ag:<id> (titolo, data, ora, assegnata_a, note) e DELETE. Prima la card aveva solo la spunta
// "fatta": una cosa scritta con il giorno o la persona sbagliati non si poteva più aggiustare.
export const scrittaAMano = (voce) => Boolean(voce && String(voce.id || '').startsWith('ag:') && voce.modificabile !== false);

// Quanto il server tiene modificando un'attività (backend/agenda.py, PUT /api/agenda/{chiave}): "Cosa fare" è il
// titolo, e ha lo stesso limite di quando l'attività si crea (200, `agenda.MAX_TITOLO`: è lo stesso campo della
// stessa riga), le "Note" mille. I campi non avevano limite: 1200 caratteri diventavano 1000 e il toast diceva solo
// "Modificata", con 200 persi in silenzio. Il taglio vale anche per l'incollato, che passa sopra `maxLength`
// (stessa regola dei campi nota, nono giro B3).
export const MAX_TITOLO_VOCE = 200;
export const MAX_TESTO_VOCE = 1000;

// La stessa voce aperta in due schede: se di là la si elimina, di qua "Salva" (o "Elimina") riceve un 404 "Voce non
// trovata" — un messaggio che non dice cos'è successo, e la riga restava lì con i campi aperti e i bottoni accesi,
// come se ci fosse ancora. Come per la visita annullata dall'altra parte ("La visita è già annullata"): si dice
// cos'è successo e si rilegge l'agenda, così la riga sparisce da sola.
export const SPARITA = "Questa cosa non c'è più: l'ha eliminata qualcun altro.";
export const eliminataAltrove = (e) => e?.response?.status === 404;

export const ModificaVoce = ({ voce, onChiudi, onFatto }) => {
  const { persone } = usePersone();
  const limiti = limitiData(isoGiorno(adessoRoma()));
  const [dati, setDati] = useState({
    titolo: voce.titolo || '', data: voce.data, ora: voce.ora || '', assegnata_a: voce.assegnata_a || '', note: voce.note || '',
  });
  const [attesa, setAttesa] = useState(false);
  const campo = { border: '1px solid var(--line2)', borderRadius: 10, padding: '.45rem .6rem' };
  const salva = async (e) => {
    e.preventDefault();
    setAttesa(true);
    try {
      await axios.put(`${API}/agenda/${voce.id}`, {
        titolo: dati.titolo, data: dati.data, ora: dati.ora || null, tutto_il_giorno: !dati.ora,
        assegnata_a: dati.assegnata_a || undefined, note: dati.note,
      });
      toast.success('Modificata');
      onFatto();
    } catch (err) {
      // eliminata da un'altra scheda: si chiude e l'agenda si rilegge (prima restavano i campi aperti su una riga
      // che non c'è più, e "Salva" si poteva ripremere all'infinito)
      if (eliminataAltrove(err)) { toast.error(SPARITA); onFatto(); return; }
      toast.error(messaggioErrore(err, 'Modifica non salvata. Riprovate.')); setAttesa(false);
    }
  };
  return (
    <form className="modifica-voce" onSubmit={salva} data-testid={`modifica-${voce.id}`}>
      {/* i campi si fermano a quello che il server tiene: oltre tagliava lui in silenzio (anche l'incollato) */}
      <input value={dati.titolo} maxLength={MAX_TITOLO_VOCE} onChange={(e) => setDati({ ...dati, titolo: e.target.value.slice(0, MAX_TITOLO_VOCE) })}
        aria-label="Cosa fare" required style={{ ...campo, flex: 1, minWidth: 180 }} />
      {/* il giorno resta dentro la finestra del server (cinque anni indietro e avanti): fuori rispondeva 400 */}
      <input type="date" value={dati.data} min={limiti.min} max={limiti.max} onChange={(e) => setDati({ ...dati, data: e.target.value })} aria-label="Giorno" style={campo} />
      <input type="time" value={dati.ora} onChange={(e) => setDati({ ...dati, ora: e.target.value })} aria-label="Ora" style={campo} />
      <select value={dati.assegnata_a} onChange={(e) => setDati({ ...dati, assegnata_a: e.target.value })} aria-label="A chi" style={campo}>
        {persone.map((p) => <option key={p.id} value={p.id}>{p.nome}</option>)}
      </select>
      <input value={dati.note} maxLength={MAX_TESTO_VOCE} onChange={(e) => setDati({ ...dati, note: e.target.value.slice(0, MAX_TESTO_VOCE) })}
        placeholder="Note" aria-label="Note" style={{ ...campo, flex: 1, minWidth: 140 }} />
      <button type="submit" className="btn sm p" disabled={attesa}>{attesa ? 'Salvo…' : 'Salva'}</button>
      <button type="button" className="btn sm" onClick={onChiudi}>Annulla</button>
    </form>
  );
};

export const EliminaVoce = ({ voce, onChiudi, onFatto }) => {
  const [attesa, setAttesa] = useState(false);
  const elimina = async () => {
    setAttesa(true);
    try {
      const r = await axios.delete(`${API}/agenda/${voce.id}`);
      // voce unita a una telefonata dell'immobile: sparisce solo la riga scritta a mano, e il server dice che la
      // telefonata resta (prima sembrava che «ricomparisse» da sola)
      toast.success(r?.data?.resta?.messaggio ? `Eliminata. ${r.data.resta.messaggio}` : 'Eliminata');
      onFatto();
    } catch (e) {
      // l'aveva già eliminata un'altra scheda: la finestra si chiude e l'agenda si rilegge, invece di restare lì
      // a chiedere di riprovare una cosa che è già successa
      if (eliminataAltrove(e)) { toast.error(SPARITA); onFatto(); return; }
      toast.error(messaggioErrore(e, 'Non eliminata. Riprovate.')); setAttesa(false);
    }
  };
  return createPortal(
    <div className="st st-modale elimina-modale" role="presentation" onMouseDown={(e) => { if (e.target === e.currentTarget) onChiudi(); }}>
      <div className="scatola" role="dialog" aria-modal="true" aria-labelledby="elim-titolo" data-testid={`elimina-${voce.id}`}>
        <h3 id="elim-titolo">Eliminare “{voce.titolo}”?</h3>
        <p className="nota">Esce dall'agenda e non si recupera. Chi l'ha scritta non riceve nessun avviso.</p>
        <div className="bottoni">
          <button type="button" className="btn sm" onClick={onChiudi}>Indietro</button>
          <button type="button" className="btn sm rosso" onClick={elimina} disabled={attesa}>{attesa ? 'Elimino…' : 'Elimina'}</button>
        </div>
      </div>
    </div>,
    document.querySelector('.mk-staff') || document.body,
  );
};

const CardVoce = ({ voce, onCambia, isAdmin = true, evidenziata = false }) => {
  const [esito, setEsito] = useState(false);
  const [modifica, setModifica] = useState(false);
  const [elimina, setElimina] = useState(false);
  const imm = voce.immobile;
  const conFoto = Boolean(imm?.foto);
  // telefonata raggruppata: l'elenco delle case sta già dentro CosaVoce, il titolo della prima sarebbe un doppione
  const inGruppo = caseDellaVoce(voce).length > 0;
  const titoloCompleto = !inGruppo && imm?.titolo && imm.titolo !== voce.titolo ? imm.titolo : null;
  const link = linkVoce(voce);
  const telefonata = voce.tipo === 'telefonata' && voce.stato === 'da_fare';
  const tel = linkChiama(voce.telefono);
  const riga = rigaVoce(voce);
  const extra = [];
  if (voce.tipo === 'visita' && imm) {
    if (prezzoBreve(imm.prezzo)) extra.push(prezzoBreve(imm.prezzo));
    if (imm.superficie) extra.push(imm.superficie);
  }
  if (voce.tipo === 'compleanno' && voce.dettaglio?.anni) extra.push(`${voce.dettaglio.anni} anni`);
  const wa = voce.tipo === 'compleanno' && linkWhatsApp(voce.telefono);
  const daEsito = visitaSenzaEsito(voce);
  const aMano = scrittaAMano(voce);
  return (
    <div className={`ev${evidenziata ? ' hi' : ''}`} data-testid={`voce-${voce.id}`}>
      <div className="corpo">
        {conFoto && <img className="foto" src={imm.foto} alt="" loading="lazy" />}
        <div style={{ minWidth: 0 }}>
          <div className={`type ${TIPI[voce.tipo]?.colore || 'altro'}`}>{etichettaTipo(voce)}</div>
          {voce.cosa ? <CosaVoce cosa={voce.cosa} tipo={voce.tipo} voce={voce} /> : <h3>{voce.titolo}</h3>}
          {titoloCompleto && <div className="imm-titolo" data-testid="titolo-immobile">{titoloCompleto}</div>}
          {((!voce.cosa && riga) || extra.length > 0 || (voce.tipo === 'visita' && imm?.indirizzo)) && (
            <p>
              {[voce.cosa ? null : riga, ...extra].filter(Boolean).join(' · ')}
              {voce.tipo === 'visita' && imm?.indirizzo && <>{(voce.cosa ? extra.length > 0 : true) ? ' · ' : ''}<a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([imm.indirizzo, imm.citta].filter(Boolean).join(', '))}`} target="_blank" rel="noopener noreferrer">Indicazioni</a></>}
            </p>
          )}
          {voce.assegnata && <div className="who"><Iniziale persona={voce.assegnata} />{voce.assegnata.nome}</div>}
        </div>
      </div>
      <div className="acts">
        {telefonata && (voce.immobile_id || voce.agenzia_id) && <button type="button" className="btn sm p" onClick={() => setEsito(!esito)} aria-expanded={esito}>Esito</button>}
        {telefonata && tel && <a className="btn sm" href={tel}>Chiama</a>}
        {wa && <a className="btn sm w" href={`${wa}?text=${encodeURIComponent(voce.dettaglio?.whatsapp || '')}`} target="_blank" rel="noopener noreferrer">WhatsApp</a>}
        {/* visita passata senza esito: "Com'è andata?"; visita ancora da fare: Sposta e Annulla */}
        {daEsito ? <EsitoVisita voce={voce} onCambia={onCambia} />
          : voce.tipo === 'visita' && voce.stato === 'da_fare' && voce.rif?.visita_id && (
            <AzioniVisita visita={visitaDaVoce(voce)} cliente={{ id: voce.cliente_id, nome: voce.cliente_nome }} onFatto={onCambia} />
          )}
        {!telefonata && link && <Link className="btn sm" to={link}>Apri</Link>}
        {/* scritta in agenda: si corregge (giorno, ora, a chi, note), si riassegna e si cancella */}
        {aMano && <button type="button" className="btn sm" onClick={() => setModifica(!modifica)} aria-expanded={modifica}>Modifica</button>}
        {aMano && <button type="button" className="btn sm rosso-vuoto" onClick={() => setElimina(true)}>Elimina</button>}
        <Spunta voce={voce} onCambia={onCambia} />
      </div>
      {modifica && <ModificaVoce voce={voce} onChiudi={() => setModifica(false)} onFatto={() => { setModifica(false); if (onCambia) onCambia(); }} />}
      {elimina && <EliminaVoce voce={voce} onChiudi={() => setElimina(false)} onFatto={() => { setElimina(false); if (onCambia) onCambia(); }} />}
      {voce.consiglio && <ConsiglioRiga consiglio={voce.consiglio} onFatto={onCambia} />}
      {/* telefonata raggruppata (più case della stessa agenzia): l'esito si dà per tutte insieme, una riga per
          casa — come in Oggi. Senza `case` il pannello resta quello di sempre (docs/piu-case-agenzia.md) */}
      {esito && <EsitoTelefonata voce={voce} isAdmin={isAdmin} case={caseEsito(voce)}
        nomeAgenzia={voce.agenzia?.etichetta || voce.agenzia?.nome} nomeCliente={voce.cliente_breve}
        onFatto={() => { setEsito(false); if (onCambia) onCambia(); }} />}
    </div>
  );
};

export default CardVoce;
